import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Space } from 'antd';
import { Button, Card, Image, Input, Radio, Select, Typography } from 'src/design-system/components/atoms';
import {
  SearchByOption,
  SELF_COURIER_PARTNER_VALUE,
} from '../GlobalTrack.constants';
import { CourierPartnerOption, OrgTrackConfig, TrackSearchPayload, TrackSearchState } from '../GlobalTrack.types';
import { splitReferenceNumbers } from '../GlobalTrack.utils';
import { fetchOrgCourierPartners, mapCourierPartners } from '../GlobalTrack.api';
import { useSearchBoxStyles } from '../GlobalTrack.styles';

const shipsyLogo: string = require('../../../../assets/images/ShipsyLogo.png');

const SHIPSY_URL = 'https://www.shipsy.ai';

interface IProps {
  initData?: TrackSearchState | null;
  onSubmit: (payload: TrackSearchPayload) => void;
  disableSubmit?: boolean;
  orgConfig: OrgTrackConfig;
}

const SearchBox = (props: IProps) => {
  const { initData, onSubmit, disableSubmit, orgConfig } = props;
  const { t } = useTranslation();
  const classes = useSearchBoxStyles();

  const [checkedOption, setCheckedOption] = React.useState<SearchByOption>(
    initData?.searchBy || SearchByOption.REFERENCE_NUMBER,
  );
  const [referenceNumberString, setReferenceNumberString] = React.useState<string>(
    initData?.numbers?.join(', ') || '',
  );
  const [selectedCourierPartner, setSelectedCourierPartner] = React.useState<string | undefined>(
    initData?.courierPartner,
  );
  const [customerCode, setCustomerCode] = React.useState<string>(initData?.code || '');
  const [courierPartnerList, setCourierPartnerList] = React.useState<CourierPartnerOption[]>([]);
  const [isCourierPartnerListLoading, setIsCourierPartnerListLoading] =
    React.useState<boolean>(false);

  const loadCourierPartners = React.useCallback(async () => {
    setIsCourierPartnerListLoading(true);
    const response = await fetchOrgCourierPartners();
    // Always include SELF and whatever partners came back (empty on failure), mirroring the source.
    setCourierPartnerList([
      { label: t('Self'), value: SELF_COURIER_PARTNER_VALUE },
      ...mapCourierPartners(response.data),
    ]);
    setIsCourierPartnerListLoading(false);
  }, [t]);

  // Re-sync from the URL-derived initData (first load + browser back/forward), mirroring the
  // source's componentWillReceiveProps.
  React.useEffect(() => {
    const mode = initData?.searchBy || SearchByOption.REFERENCE_NUMBER;
    setCheckedOption(mode);
    setReferenceNumberString(initData?.numbers?.join(', ') || '');
    setSelectedCourierPartner(initData?.courierPartner);
    setCustomerCode(initData?.code || '');
    if (
      mode === SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER &&
      !courierPartnerList.length &&
      !isCourierPartnerListLoading
    ) {
      loadCourierPartners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData]);

  const handleModeChange = (value: SearchByOption) => {
    setCheckedOption(value);
    if (
      value === SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER &&
      !courierPartnerList.length &&
      !isCourierPartnerListLoading
    ) {
      loadCourierPartners();
    }
  };

  const handleSubmit = () => {
    onSubmit({
      searchBy: checkedOption,
      numbers: splitReferenceNumbers(referenceNumberString),
      code: checkedOption === SearchByOption.CUSTOMER_REFERENCE_NUMBER ? customerCode : undefined,
      courierPartner:
        checkedOption === SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER
          ? selectedCourierPartner
          : undefined,
    });
  };

  return (
    <Card className={classes.card} title={t('Track Shipment')}>
      <div className={classes.radioGroup}>
        <Typography.Text className={classes.label}>{t('Track By')}</Typography.Text>
        <Radio.Group value={checkedOption} onChange={(e) => handleModeChange(e.target.value)}>
          <Row gutter={16} className={classes.radioRow}>
            <Col xs={24} sm={8}>
              <Radio value={SearchByOption.REFERENCE_NUMBER}>{t('Reference No.')}</Radio>
            </Col>
            {!orgConfig.hideSearchByPartnerNumber ? (
              <Col xs={24} sm={8}>
                <Radio value={SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER}>
                  {t('Partner AWB No.')}
                </Radio>
              </Col>
            ) : null}
            {!orgConfig.hideSearchByCustomerReferenceNumber ? (
              <Col xs={24} sm={8}>
                <Radio value={SearchByOption.CUSTOMER_REFERENCE_NUMBER}>
                  {t('Customer Reference No.')}
                </Radio>
              </Col>
            ) : null}
          </Row>
        </Radio.Group>
      </div>

      {checkedOption === SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER ? (
        <div className={classes.field}>
          <Typography.Text className={classes.label}>{t('Courier Partner')}</Typography.Text>
          <Select
            className={classes.select}
            showSearch
            optionFilterProp="label"
            loading={isCourierPartnerListLoading}
            options={courierPartnerList}
            value={selectedCourierPartner}
            placeholder={t('Select Courier Partner')}
            onChange={(value) => setSelectedCourierPartner(value as string)}
          />
        </div>
      ) : null}

      {checkedOption === SearchByOption.CUSTOMER_REFERENCE_NUMBER ? (
        <div className={classes.field}>
          <Typography.Text className={classes.label}>{t('Customer Code')}</Typography.Text>
          <Input
            value={customerCode}
            placeholder={t('Enter Customer Code')}
            onChange={(e) => setCustomerCode(e.target.value)}
            onPressEnter={handleSubmit}
          />
        </div>
      ) : null}

      <div className={classes.field}>
        <Typography.Text className={classes.label}>
          {t('Enter up to 30 comma separated reference numbers')}
        </Typography.Text>
        <Input
          allowClear
          value={referenceNumberString}
          placeholder={
            checkedOption === SearchByOption.COURIER_PARTNER_REFERENCE_NUMBER
              ? t('Partner AWB Numbers')
              : t('Reference Numbers')
          }
          onChange={(e) => setReferenceNumberString(e.target.value)}
          onPressEnter={handleSubmit}
        />
      </div>

      <div className={classes.footer}>
        {!orgConfig.hidePoweredByShipsy ? (
          <a href={SHIPSY_URL} target="_blank" rel="noopener noreferrer" className={classes.poweredBy}>
            <Space size={6} align="center">
              <Typography.Text type="secondary">{t('Powered by')}</Typography.Text>
              <Image src={shipsyLogo} alt="Shipsy" preview={false} height={20} />
            </Space>
          </a>
        ) : null}
        <Button
          type="primary"
          style={{ marginLeft: 'auto' }}
          onClick={handleSubmit}
          disabled={disableSubmit}
        >
          {t('Track')}
        </Button>
      </div>
    </Card>
  );
};

export default SearchBox;
